import React, { Component, Fragment } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../../i18n/FormattedMessage';
import { chatSocketHandlers } from '../../../socket';
import {
  SearchIcon,
  EllipsisVerticalIcon,
} from '../../../components_2/AriesIcons';
import MessageBox, { MESSAGE_TYPES } from './MessageBox';
import ChatBodyFooter from './ChatBodyFooter';
import ProfilePhoto from './ProfilePhoto';

const SCROLL_THRESHOLD = 50;
const LIMIT = 30;

export const ChatBodyHeader = ({
  senderName,
  senderImg,
  subtitle,
  isLoading,
  showChatRoomDetails,
  isGroup,
  onBackClick,
  onSearchToggle,
}) => (
  <div className="chat-header clearfix">
    <div
      className="chat-header-info"
      onClick={isGroup ? showChatRoomDetails : undefined}
    >
      <h1 className="head-back">
        <span
          className="back-arrow sprite-icon chat-header-back"
          onClick={onBackClick}
        />
      </h1>
      <ProfilePhoto label={senderName} size="30px" src={senderImg} />
      <div className="chat-about">
        <div className="chat-with">{senderName}</div>
        {subtitle ? <div className="chat-with-subtitle">{subtitle}</div> : null}
      </div>
      {isLoading && (
        <img className="chat-wrap-loader" src="./img/loading.gif" />
      )}
    </div>
    <div className="chat-header-actions">
      <button
        type="button"
        className="chat-header-action"
        aria-label="Search in conversation"
        onClick={onSearchToggle}
      >
        <SearchIcon size={20} />
      </button>
      <button
        type="button"
        className="chat-header-action"
        aria-label="Conversation options"
        onClick={isGroup ? showChatRoomDetails : undefined}
      >
        <EllipsisVerticalIcon size={20} />
      </button>
    </div>
  </div>
);

ChatBodyHeader.propTypes = {
  isGroup: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  senderName: PropTypes.string.isRequired,
  senderImg: PropTypes.string,
  subtitle: PropTypes.string,
  showChatRoomDetails: PropTypes.func.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onSearchToggle: PropTypes.func.isRequired,
};

const ChatEmptyIllustration = () => (
  <svg
    className="chat-empty-illustration"
    width="140"
    height="104"
    viewBox="0 0 140 104"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="10"
      y="14"
      width="86"
      height="58"
      rx="12"
      fill="#f4f6f8"
      stroke="#e5e7eb"
      strokeWidth="2"
    />
    <path
      d="M30 78l12-10h4v18z"
      fill="#f4f6f8"
      stroke="#e5e7eb"
      strokeWidth="2"
    />
    <rect x="24" y="30" width="58" height="6" rx="3" fill="#e5e7eb" />
    <rect x="24" y="44" width="40" height="6" rx="3" fill="#e5e7eb" />
    <circle cx="104" cy="56" r="26" fill="#b71b1b" />
    <rect x="92" y="50" width="24" height="4" rx="2" fill="#ffffff" />
    <rect x="92" y="58" width="16" height="4" rx="2" fill="#ffffff" />
  </svg>
);

const ChatEmptyState = ({ isGroupChatEnabled }) => (
  <div className="chat-empty-state">
    <ChatEmptyIllustration />
    <p className="chat-empty-title">
      {isGroupChatEnabled ? (
        <FormattedMessage
          id="chat.emptyGroupTitle"
          defaultMessage="Chat With Your Team Groups"
        />
      ) : (
        <FormattedMessage
          id="chat.emptyIndividualTitle"
          defaultMessage="Chat Individually With Team Members"
        />
      )}
    </p>
    <p className="chat-empty-subtitle">
      {isGroupChatEnabled ? (
        <FormattedMessage
          id="noChatGroup"
          defaultMessage="Click the 'Start New Group Chat' to start chatting with your team members."
        />
      ) : (
        <FormattedMessage
          id="noChatIndividual"
          defaultMessage="Click the 'Start New Chat' to start chatting with your team members."
        />
      )}
    </p>
  </div>
);

ChatEmptyState.propTypes = {
  isGroupChatEnabled: PropTypes.bool.isRequired,
};

const getMessageType = (messageBody, currentUserId) => {
  if (messageBody.isSystem) {
    return MESSAGE_TYPES.system;
  }
  if (messageBody.senderId === currentUserId) {
    return MESSAGE_TYPES.sentText;
  }
  return MESSAGE_TYPES.receivedText;
};

class ChatBody extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chatRoomInfo: {},
      prevActiveChatId: '',
      prevPeopleList: [],
      isSearchVisible: false,
      searchTerm: '',
    };
    this.isPaginated = false;
    this.messagesContainer = React.createRef();
  }

  componentDidMount() {
    // When arriving from the header chat popover, activeChatId is already set
    // in the store before this view mounts, so componentDidUpdate never sees a
    // prevProps.activeChatId !== activeChatId transition and the initial fetch
    // never fires (messages show empty until switching chats). Trigger the
    // load here if a chat is already active at mount time.
    if (this.props.activeChatId) {
      this.onChangeActiveChat();
    }
  }

  componentWillUnmount() {
    if (this.messagesContainer.current) {
      this.messagesContainer.current.removeEventListener(
        'scroll',
        this.onScroll
      );
    }
  }

  onScroll = () => {
    if (this.messagesContainer.current.scrollTop < SCROLL_THRESHOLD) {
      this.loadMore();
    }
  };

  loadMore = () => {
    this.isPaginated = true;
    const {
      chats,
      activeChatId,
      currentUser,
      getAllChatMessages,
      isChatLoading,
      totalChatMessagesCount,
    } = this.props;
    const activeChat = chats[activeChatId] || [];
    if (!isChatLoading && totalChatMessagesCount > activeChat.length) {
      getAllChatMessages(
        { chatId: activeChatId, currentUserId: currentUser.customerId }, // payload
        activeChat.length, // skip
        LIMIT, // limit
        true // isPaginatedRequest
      );
    }
  };

  static getDerivedStateFromProps(props, state) {
    if (
      state.prevActiveChatId !== props.activeChatId ||
      props.peopleList !== state.prevPeopleList
    ) {
      return {
        prevActiveChatId: props.activeChatId,
        chatRoomInfo:
          props.peopleList.find((item) => item.chatId === props.activeChatId) ||
          {},
      };
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    const { activeChatId, isGetChatByIdSuccess, chats } = this.props;
    if (activeChatId && this.messagesContainer.current) {
      this.messagesContainer.current.addEventListener(
        'scroll',
        this.onScroll,
        false
      );
    }
    if (activeChatId && prevProps.activeChatId !== activeChatId) {
      this.onChangeActiveChat();
    }
    const activeChat = chats[activeChatId] || [];
    const prevActiveChat = prevProps.chats[prevProps.activeChatId] || [];
    const willScroll =
      (isGetChatByIdSuccess &&
        prevProps.isGetChatByIdSuccess !== isGetChatByIdSuccess) ||
      activeChat.length !== prevActiveChat.length;
    if (willScroll && !this.isPaginated) {
      this.scrollToBottom();
    }
  }

  onChangeActiveChat = () => {
    this.isPaginated = false;
    const { activeChatId, getAllChatMessages, currentUser } = this.props;
    getAllChatMessages(
      { chatId: activeChatId, currentUserId: currentUser.customerId },
      0, // skip
      LIMIT, // limit
      false // isPaginated
    );
    this.getGroupDetails();
    chatSocketHandlers.emitChatIdAsSeen({
      chatId: activeChatId,
      currentUserId: currentUser.customerId,
    });
  };

  handleMessageSend = (msgText) => {
    const { sendTextMessage, currentUser, activeChatId } = this.props;
    sendTextMessage({
      content: msgText,
      currentUser,
      activeChatId,
    });
    this.scrollToBottom();
  };

  scrollToBottom = () => {
    if (this.messagesContainer.current) {
      const { scrollHeight } = this.messagesContainer.current;
      const height = this.messagesContainer.current.clientHeight;
      const maxScrollTop = scrollHeight - height;
      this.messagesContainer.current.scrollTop =
        maxScrollTop > 0 ? maxScrollTop : 0;
    }
  };

  getGroupDetails = () => {
    const { activeChatId, getGroupDetails } = this.props;
    getGroupDetails({ chatId: activeChatId });
  };

  onMessageClick = (msg) => {
    const { currentUser, activeChatId, deleteOldMessage } = this.props;
    deleteOldMessage(msg, currentUser, activeChatId);
    this.handleMessageSend(msg.content);
  };

  toggleSearch = () => {
    this.setState((prev) => ({
      isSearchVisible: !prev.isSearchVisible,
      searchTerm: prev.isSearchVisible ? '' : prev.searchTerm,
    }));
  };

  onSearchTermChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  render() {
    const {
      chats,
      activeChatId,
      isChatLoading,
      currentUser,
      isUpdateChatLoading,
      showChatRoomDetails,
      isChatRoomDetailsLoading,
      setActiveChatId,
      isGroupChatEnabled,
    } = this.props;
    const {
      chatRoomInfo: { senderName, senderImg, isGroup, subtitle },
      isSearchVisible,
      searchTerm,
    } = this.state;
    const activeChat = chats[activeChatId] || [];
    const term = searchTerm.trim().toLowerCase();
    const visibleChat =
      isSearchVisible && term
        ? activeChat.filter((msg) =>
            (msg.content || '').toLowerCase().includes(term)
          )
        : activeChat;
    return (
      <div className="chat chat-responsive">
        {activeChatId && (
          <Fragment>
            <ChatBodyHeader
              senderName={senderName}
              subtitle={subtitle}
              senderImg={senderImg}
              isLoading={
                isChatLoading || isUpdateChatLoading || isChatRoomDetailsLoading
              }
              getGroupDetails={this.getGroupDetails}
              showChatRoomDetails={showChatRoomDetails}
              isGroup={isGroup}
              activeChatId={activeChatId}
              onBackClick={() => setActiveChatId('')}
              onSearchToggle={this.toggleSearch}
            />
            {isSearchVisible && (
              <div className="chat-inline-search">
                <span className="chat-inline-search-icon material-icons-outlined">
                  search
                </span>
                <input
                  type="text"
                  className="chat-inline-search-input"
                  value={searchTerm}
                  onChange={this.onSearchTermChange}
                  placeholder={this.props.intl.formatMessage({
                    id: 'chat.searchInConversation',
                    defaultMessage: 'Search in conversation',
                  })}
                />
              </div>
            )}
            <div
              className="mCustomScrollbar chat-history-wrap"
              data-mcs-theme="dark"
            >
              <div className="chat-history" ref={this.messagesContainer}>
                <ul>
                  {visibleChat.map((msg) => {
                    const isMine = msg.senderId === currentUser.customerId;
                    // Use the real profile photo: mine for my messages, the
                    // other member's for received ones (resolved from backend).
                    const avatarSrc = isMine
                      ? currentUser.profilePicture
                      : senderImg;
                    return (
                      <MessageBox
                        key={msg.clientMsgId || msg.msgId}
                        content={msg.content}
                        type={getMessageType(msg, currentUser.customerId)}
                        msgTime={msg.msgTime}
                        senderImg={avatarSrc}
                        msgStatus={msg.msgStatus}
                        senderName={msg.senderName}
                        onMessageClick={() => this.onMessageClick(msg)}
                      />
                    );
                  })}
                </ul>
              </div>
            </div>
            <ChatBodyFooter
              onSendPress={this.handleMessageSend}
              isChatLoading={isChatLoading}
              activeChatId={activeChatId}
            />
          </Fragment>
        )}
        {!activeChatId && (
          <ChatEmptyState isGroupChatEnabled={isGroupChatEnabled} />
        )}
      </div>
    );
  }
}

ChatBody.propTypes = {
  activeChatId: PropTypes.string.isRequired,
  chats: PropTypes.array.isRequired,
  currentUser: PropTypes.object.isRequired,
  getAllChatMessages: PropTypes.func.isRequired,
  getGroupDetails: PropTypes.func.isRequired,
  isChatLoading: PropTypes.bool.isRequired,
  totalChatMessagesCount: PropTypes.number.isRequired,
  isChatRoomDetailsLoading: PropTypes.bool.isRequired,
  isGetChatByIdSuccess: PropTypes.bool.isRequired,
  isUpdateChatLoading: PropTypes.bool.isRequired,
  peopleList: PropTypes.array.isRequired,
  sendTextMessage: PropTypes.func.isRequired,
  showChatRoomDetails: PropTypes.func.isRequired,
  setActiveChatId: PropTypes.func.isRequired,
  deleteOldMessage: PropTypes.func.isRequired,
  isGroupChatEnabled: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(ChatBody);
